import axiosInstance from '../../api/api';
import { API_ROUTES } from '../../config/api-routes';
import { useSessionStore } from '../../store/sessionStore';
import { normalizeAppPrompt } from './promptsV2Utils';
import { resolveCandidateProfileMeta } from './promptsV2RolePrompts';
import { uploadPromptVideoDirectWithSavePhase } from './promptVideoUpload';
import type { PromptVideoUploadProgress } from './promptVideoUpload';
import type { AppPrompt, CandidateProfileMeta, PromptAnalyticsRow } from './promptsV2Types';

const PROMPT_ANALYTICS_TTL_MS = 60 * 1000;
let promptAnalyticsCache: PromptAnalyticsRow[] | null = null;
let promptAnalyticsFetchedAt: number | null = null;
let promptAnalyticsInFlight: Promise<PromptAnalyticsRow[]> | null = null;

export function invalidatePromptAnalyticsCache() {
  promptAnalyticsCache = null;
  promptAnalyticsFetchedAt = null;
  promptAnalyticsInFlight = null;
}

export async function loadPromptsWorkspace() {
  const { user, prompts } = await useSessionStore.getState().bootstrapSession();
  const list = (Array.isArray(prompts) ? prompts : []) as AppPrompt[];
  const profile = resolveCandidateProfileMeta(user as Record<string, unknown> | null, list);
  return {
    profile,
    prompts: list,
    user: (user as Record<string, unknown> | null) || null,
  };
}

function appendPromptJson(formData: FormData, payload: Record<string, unknown>) {
  formData.append('prompts', JSON.stringify([payload]));
}

async function savePromptRecord(
  promptId: number | null,
  payload: Record<string, unknown>,
  docFile: File | null
) {
  const record = { ...payload };
  const formData = new FormData();
  if (docFile) {
    const idKey = promptId ?? Date.now();
    formData.append(`doc-${idKey}`, docFile, docFile.name);
    record.supportDocumentFileId = `doc-${idKey}`;
  }
  appendPromptJson(formData, record);

  if (promptId != null) {
    const response = await axiosInstance.put(`prompt/${promptId}`, formData);
    invalidatePromptAnalyticsCache();
    const data = response.data as Record<string, unknown>;
    return normalizeAppPrompt(data && typeof data === 'object' ? data : { id: promptId });
  }

  const response = await axiosInstance.post('prompt', formData);
  invalidatePromptAnalyticsCache();
  const data = response.data;
  if (Array.isArray(data)) {
    const last = data[data.length - 1];
    return normalizeAppPrompt(
      last && typeof last === 'object' ? (last as Record<string, unknown>) : { id: 0 }
    );
  }
  return normalizeAppPrompt(
    data && typeof data === 'object' ? (data as Record<string, unknown>) : { id: 0 }
  );
}

export async function createPromptWithMedia(params: {
  content: string;
  description: string;
  profile: CandidateProfileMeta;
  videoFile: File | null;
  docFile: File | null;
  onVideoProgress?: (progress: PromptVideoUploadProgress) => void;
  uploadClientId?: string;
}): Promise<AppPrompt> {
  const basePayload = {
    content: params.content,
    description: params.description,
    jobTitle: params.profile.jobTitle || '',
    jobIndustry: params.profile.jobIndustry || '',
    level: params.profile.level || '',
  };

  if (!params.videoFile) {
    return savePromptRecord(null, basePayload, params.docFile);
  }

  let saved: AppPrompt | null = null;
  await uploadPromptVideoDirectWithSavePhase(
    params.videoFile,
    {
      clientId: params.uploadClientId,
      promptId: null,
      onProgress: params.onVideoProgress,
    },
    async (publicUrl) => {
      saved = await savePromptRecord(null, { ...basePayload, fileUrl: publicUrl }, params.docFile);
    }
  );
  if (!saved) {
    throw new Error('Prompt save failed after video upload.');
  }
  return saved;
}

export async function updatePromptWithMedia(
  promptId: number,
  params: {
    content: string;
    description: string;
    visibility?: string;
    videoFile: File | null;
    docFile: File | null;
    onVideoProgress?: (progress: PromptVideoUploadProgress) => void;
    uploadClientId?: string;
  }
): Promise<AppPrompt> {
  const basePayload = {
    id: promptId,
    content: params.content,
    description: params.description,
    visibility: params.visibility,
  };

  if (!params.videoFile) {
    return savePromptRecord(promptId, basePayload, params.docFile);
  }

  let saved: AppPrompt | null = null;
  await uploadPromptVideoDirectWithSavePhase(
    params.videoFile,
    {
      clientId: params.uploadClientId,
      promptId,
      onProgress: params.onVideoProgress,
    },
    async (publicUrl) => {
      saved = await savePromptRecord(
        promptId,
        { ...basePayload, fileUrl: publicUrl },
        params.docFile
      );
    }
  );
  if (!saved) {
    throw new Error('Prompt update failed after video upload.');
  }
  return saved;
}

export async function deletePromptById(promptId: number) {
  await axiosInstance.delete(`prompt/${promptId}`);
  await useSessionStore.getState().refreshPrompts({ force: true });
  invalidatePromptAnalyticsCache();
}

export async function fetchPromptsList() {
  const prompts = await useSessionStore.getState().refreshPrompts();
  return (Array.isArray(prompts) ? prompts : []) as AppPrompt[];
}

export async function fetchPromptAnalytics(): Promise<PromptAnalyticsRow[]> {
  if (
    promptAnalyticsCache &&
    promptAnalyticsFetchedAt &&
    Date.now() - promptAnalyticsFetchedAt < PROMPT_ANALYTICS_TTL_MS
  ) {
    return promptAnalyticsCache;
  }
  if (promptAnalyticsInFlight) {
    return promptAnalyticsInFlight;
  }

  promptAnalyticsInFlight = (async () => {
    try {
      const response = await axiosInstance.get(API_ROUTES.ANALYTICS.PROMPT_OVERVIEW);
      const rows = response.data?.data ?? response.data;
      if (!Array.isArray(rows)) {
        promptAnalyticsCache = [];
        promptAnalyticsFetchedAt = Date.now();
        return [];
      }
      const mapped = rows.map((row: Record<string, unknown>) => {
        const views = Number(row.views) || 0;
        const likes = Number(row.likes) || 0;
        const impressions = Number(row.impressions) || 0;
        const rawScore = Number(row.score);
        const score = Number.isFinite(rawScore) ? rawScore : likes * 5 + views * 1;
        return {
          promptId: Number(row.promptId),
          views,
          impressions,
          likes,
          score,
        };
      });
      promptAnalyticsCache = mapped;
      promptAnalyticsFetchedAt = Date.now();
      return mapped;
    } catch {
      return [];
    } finally {
      promptAnalyticsInFlight = null;
    }
  })();
  return promptAnalyticsInFlight;
}

export async function loadPromptsWorkspaceWithAnalytics() {
  const [workspace, analytics] = await Promise.all([
    loadPromptsWorkspace(),
    fetchPromptAnalytics(),
  ]);
  return { ...workspace, analytics };
}
